import { useState, useEffect } from 'react';
import { Modal, Input, Button, Form, Message, Loader, InputNumber } from 'rsuite';
import { useAuth } from '../utils/useAuth';
import { getApiUrl, getAuthHeaders, getUserId } from '../utils/apiConfig';

function PaymentDialog({ show, onClose, onPaymentSuccess }) {
  const auth = useAuth();
  const [amount, setAmount] = useState(100);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(null);
  const [fetchingBalance, setFetchingBalance] = useState(false);

  // Fetch current balance when dialog opens
  useEffect(() => {
    if (show && auth.hasAuth0 && auth.isAuthenticated) {
      fetchCurrentBalance();
    }
  }, [show]);

  const fetchCurrentBalance = async () => {
    setFetchingBalance(true);
    setError('');
    
    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      
      const response = await fetch(getApiUrl('/api/users/me/balance'), {
        method: 'GET',
        headers: headers
      });

      if (response.status === 503) {
        // Billing not configured
        setCurrentBalance(null);
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail?.message || data.detail || 'Failed to fetch balance');
      }

      if (data.status === 'success') {
        setCurrentBalance(data.balance);
      }
    } catch (err) {
      console.error('Failed to fetch balance:', err);
      setError('Failed to load current balance');
    } finally {
      setFetchingBalance(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess(false);
    
    // Validate amount
    if (!amount || amount <= 0) {
      setError('Amount must be greater than $0');
      return;
    }

    if (amount > 10000) {
      setError('Maximum payment amount is $10,000');
      return;
    }

    setLoading(true);

    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      
      // Create form data for the payment
      const formData = new FormData();
      formData.append('amount', amount.toString());

      const response = await fetch(getApiUrl('/api/users/me/payment'), {
        method: 'POST',
        headers: {
          'Authorization': headers['Authorization']
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail?.message || data.detail?.error || data.detail || 'Failed to create checkout session');
      }

      if (data.status === 'success' && data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Failed to create checkout session. Please try again.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setSuccess(false);
      setAmount(100);
      onClose();
    }
  };

  const presetAmounts = [10, 50, 100, 500, 1000];

  return (
    <Modal open={show} onClose={handleClose} size="sm">
      <Modal.Header>
        <Modal.Title>Add Funds</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {fetchingBalance ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Loader size="md" content="Loading balance..." />
          </div>
        ) : (
          <Form fluid onSubmit={handleSubmit}>
            {error && (
              <Message type="error" showIcon style={{ marginBottom: 20 }}>
                {error}
              </Message>
            )}

            {success && (
              <Message type="success" showIcon style={{ marginBottom: 20 }}>
                Payment processed successfully! Your balance will be updated shortly.
              </Message>
            )}
            
            <Form.Group>
              <Form.ControlLabel>Amount (USD)</Form.ControlLabel>
              <InputNumber
                value={amount}
                onChange={(value) => {
                  const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
                  setAmount(numValue);
                }}
                min={1}
                max={10000}
                step={1}
                prefix="$"
                disabled={loading || success}
                style={{ width: '100%' }}
              />
              <Form.HelpText>
                Enter the amount you want to add (minimum $1, maximum $10,000)
              </Form.HelpText>
            </Form.Group>

            <Form.Group>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                {presetAmounts.map(preset => (
                  <Button
                    key={preset}
                    size="sm"
                    appearance={amount === preset ? 'primary' : 'subtle'}
                    onClick={() => setAmount(preset)}
                    disabled={loading || success}
                  >
                    ${preset}
                  </Button>
                ))}
              </div>
            </Form.Group>

            <Form.Group>
              <Button 
                appearance="primary" 
                type="submit" 
                block 
                loading={loading}
                disabled={success}
              >
                {success ? 'Processing...' : `Add $${amount.toFixed(2)}`}
              </Button>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default PaymentDialog;

